import React, { useState } from 'react';
import { Flag, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';

interface ReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  contentType: 'topic' | 'reply';
  onSuccess: () => void;
}

const REPORT_REASONS = [
  'Inappropriate content',
  'Harassment or bullying',
  'Spam or advertising',
  'Other'
];

export const ReportDialog: React.FC<ReportDialogProps> = ({
  isOpen,
  onClose,
  contentId,
  contentType,
  onSuccess
}) => {
  const { isDarkMode } = useTheme();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError('Please select a reason for reporting');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get current user
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get user's database ID
      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userDataError) throw userDataError;
      if (!userData) throw new Error('User not found');

      // Create report
      const { error: reportError } = await supabase.from('forum_reports').insert({
        content_id: contentId,
        content_type: contentType,
        reporter_id: userData.id,
        reason: selectedReason,
        additional_info: additionalInfo,
        status: 'pending'
      });

      if (reportError) throw reportError;

      // Success
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'sm:max-w-[425px]',
          isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-light-100 border-light-300'
        )}
      >
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
            <Flag className="h-6 w-6 text-red-500" />
          </div>
          <DialogTitle
            className={cn(
              'text-center text-lg',
              isDarkMode ? 'text-white' : 'text-light-900'
            )}
          >
            Report {contentType === 'topic' ? 'Topic' : 'Reply'}
          </DialogTitle>
          <DialogDescription
            className={cn(
              'text-center',
              isDarkMode ? 'text-dark-800' : 'text-light-600',
              'bg-red-500/10 text-red-500 text-xs'
            )}
          >
            Help us maintain a respectful community by reporting inappropriate content.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label
              className={cn(
                isDarkMode ? 'text-dark-800' : 'text-light-600',
                'text-xs'
              )}
            >
              Reason for reporting
            </Label>
            <div className="grid grid-cols-1 gap-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setSelectedReason(reason)}
                  className={cn(
                    'flex items-center px-4 py-2 rounded-lg text-left transition-colors',
                    selectedReason === reason
                      ? 'bg-gold-400/20 border border-gold-400/50'
                      : 'border border-dark-300 hover:bg-dark-300/50',
                    isDarkMode ? 'text-white' : 'text-light-900',
                    'text-sm'
                  )}
                >
                  {reason}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="additional-info"
              className={cn(
                isDarkMode ? 'text-dark-800' : 'text-light-600',
                'text-xs'
              )}
            >
              Additional information (optional)
            </Label>
            <Textarea
              id="additional-info"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder="Please provide any additional details that might help us understand the issue..."
              className={cn(
                isDarkMode
                  ? 'bg-dark-300 border-dark-400 text-white text-sm'
                  : 'bg-light-200 border-light-300 text-light-900 text-sm'
              )}
            />
          </div>

          {error && (
            <div
              className={cn(
                'p-3 rounded-lg flex items-center',
                'bg-red-500/10 text-red-500'
              )}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-center">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className={cn(
                'sm:order-1 border-dark-400',
                isDarkMode ? 'text-white hover:bg-dark-400' : 'text-gray-700 hover:bg-gray-100',
                'text-sm'
              )}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedReason}
              className={cn(
                'bg-red-500 hover:bg-red-600 text-white sm:order-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'text-sm'
              )}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
